import { inject, Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, tap } from 'rxjs';
import { AppRoutes } from '../routes';

@Injectable({ providedIn: 'root' })
export class NavigationService implements OnDestroy {
  private readonly router: Router = inject(Router);
  private _history!: string[];
  private _previousUrl: string | null = null;
  private _currentUrl: string | null = null;
  private subscription: Subscription = Subscription.EMPTY;

  get history(): string[] {
    return this._history?.filter((h: string) => h.toLowerCase() !== this.router.url?.toLowerCase()) || [];
  }

  get previousUrl(): string | null {
    return this._previousUrl;
  }

  get hasHistory(): boolean {
    return !!this.history?.length;
  }

  init(): void {
    if (this._history) {
      return;
    }

    this._history = [];
    this.subscription = this.router.events
      .pipe(
        tap((nav: unknown) => {
          if (nav instanceof NavigationEnd) {
            this._previousUrl = this._currentUrl;
            this._currentUrl = nav.urlAfterRedirects;

            const historySize: number = this._history.length;
            const hasTwoItems: boolean = historySize > 1;
            if (hasTwoItems && this._history[historySize - 2] === nav.urlAfterRedirects) {
              this._history.pop();
              return;
            }

            if (!historySize || (historySize && this._history[historySize - 1] !== nav.urlAfterRedirects))
              this._history.push(nav.urlAfterRedirects);
          }
        })
      )
      .subscribe();
  }

  previousPage(): string {
    return this._history?.length > 0 ? this._history[this._history.length - 1] : AppRoutes.ROOT;
  }

  back(): void {
    if (!this.hasHistory) {
      this.router.navigateByUrl(AppRoutes.ROOT);
      return;
    }

    this._history.pop();
    const backUrl: string = this._history?.length > 0 ? this._history[this._history.length - 1] : AppRoutes.ROOT;
    this.router.navigateByUrl(backUrl);
  }

  // Nawiguje, ale najpierw zdejmuje bieżącą stronę ze stosu (np. formularz po zapisie),
  // żeby cofnięcie z docelowego ekranu wracało do miejsca sprzed formularza, nie do niego.
  replace(commands: unknown[]): void {
    if (this._history?.length) {
      this._history.pop();
    }
    void this.router.navigate(commands as never[]);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
